import type React from 'react';
import classNames from 'classnames';

import styles from './SvgIcon.module.scss';

/** An SVG imported with `?component` from `assets/icons`, the portal's one icon set. */
export type IconComponent = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

export interface SvgIconProps {
    icon: IconComponent;
    /** `inherit` sizes the icon at 1em, like an icon-font glyph in running text. */
    size?: 'small' | 'medium' | 'large' | 'custom' | 'inherit';
    /** Pixels, or any CSS length such as `1.7em`. */
    customSize?: number | string;
    color?: string;
    className?: string;
    /** Rotates the icon continuously, for loading indicators. */
    spin?: boolean;
    /** Reserves 1.25em, so icons of different widths swapped in one place do not move the text. */
    fixedWidth?: boolean;
    svgProps?: React.SVGProps<SVGSVGElement>;
}

export function SvgIcon({
    icon: IconComponent,
    size = 'medium',
    customSize,
    color,
    className,
    spin = false,
    fixedWidth = false,
    svgProps = {},
    ...rest
}: SvgIconProps) {
    const getSizeValue = () => {
        switch (size) {
            case 'small':
                return 16;
            case 'medium':
                return 24;
            case 'large':
                return 32;
            case 'custom':
                return customSize || 24;
            case 'inherit':
                return '1em';
            default:
                return 24;
        }
    };

    const sizeValue = getSizeValue();

    return (
        <IconComponent
            width={sizeValue}
            height={sizeValue}
            className={classNames(styles.svgIcon, className, {
                [styles.inheritSize]: size === 'inherit',
                [styles.spin]: spin,
                [styles.fixedWidth]: fixedWidth,
            })}
            style={{ color: color }}
            {...svgProps}
            {...rest}
        />
    );
}
